import { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

interface MonthYearPickerProps {
  value: string; // "YYYY-MM" or "YYYY" or ""
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowYearOnly?: boolean;
}

export function MonthYearPicker({
  value,
  onChange,
  placeholder = "Select date",
  className,
  disabled = false,
  allowYearOnly = false,
}: MonthYearPickerProps) {
  const [open, setOpen] = useState(false);

  // Parse current value
  const currentYear = new Date().getFullYear();
  let selectedYear = currentYear;
  let selectedMonth: number | null = null;

  if (value) {
    const parts = value.split("-");
    if (parts[0]) selectedYear = parseInt(parts[0], 10) || currentYear;
    if (parts[1]) selectedMonth = parseInt(parts[1], 10) - 1; // 0-indexed
  }

  const [displayYear, setDisplayYear] = useState(selectedYear);

  useEffect(() => {
    if (value) {
      const parts = value.split("-");
      if (parts[0]) setDisplayYear(parseInt(parts[0], 10) || currentYear);
    }
  }, [value]);

  const handleMonthClick = useCallback((monthIndex: number) => {
    const yyyy = String(displayYear);
    const mm = String(monthIndex + 1).padStart(2, "0");
    onChange(`${yyyy}-${mm}`);
    setOpen(false);
  }, [displayYear, onChange]);

  const handleYearOnly = useCallback(() => {
    onChange(String(displayYear));
    setOpen(false);
  }, [displayYear, onChange]);

  const handleClear = useCallback(() => {
    onChange("");
    setOpen(false);
  }, [onChange]);

  // Format display value
  const displayValue = value
    ? value.includes("-")
      ? `${MONTHS[parseInt(value.split("-")[1], 10) - 1]} ${value.split("-")[0]}`
      : value
    : "";

  const yearOptions = [];
  for (let y = currentYear + 5; y >= 1970; y--) {
    yearOptions.push(y);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-10",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {displayValue || placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-3 pointer-events-auto" align="start">
        {/* Year navigation */}
        <div className="flex items-center justify-between mb-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setDisplayYear((y) => y - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Select
            value={String(displayYear)}
            onValueChange={(v) => setDisplayYear(parseInt(v, 10))}
          >
            <SelectTrigger className="w-[100px] h-8 text-sm font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setDisplayYear((y) => y + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Month grid */}
        <div className="grid grid-cols-3 gap-1.5">
          {MONTHS.map((month, i) => {
            const isSelected = selectedMonth === i && selectedYear === displayYear;
            return (
              <Button
                key={month}
                variant={isSelected ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "h-9 text-sm",
                  isSelected && "bg-primary text-primary-foreground"
                )}
                onClick={() => handleMonthClick(i)}
              >
                {month}
              </Button>
            );
          })}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground"
            onClick={handleClear}
          >
            Clear
          </Button>
          {allowYearOnly && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground"
              onClick={handleYearOnly}
            >
              Year only
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
